import { useAuth } from "@/features/Auth";
import { useLocation } from "react-router-dom";
import { clientConfig } from "@/features/ClientActions/button_config";
import { useMemo } from "react";
import { useWorkspace } from "@/features/Workspaces/WorkspaceContext";
import { normalizeCode } from "@/features/Workspaces/deriveWorkspaces";

/**
 *
 * For client "s1-instit" for instance the request will be
 * http://localhost:5173/w/client/s1%20-%20instit/admin/s1%20-%20instit/s1_download_center
 * This method will transform "s1%20-%20instit" to "s1-instit"
 */
function decodePathSegment(value: string | undefined): string | undefined {
  if (!value) return value;
  try {
    return decodeURIComponent(value).replace(/\s+/g, "");
  } catch {
    return value.replace(/\s+/g, "");
  }
}

/**
 * Hook to determine the current client based on the URL
 *
 * This hook analyzes the URL path to determine which client the user is currently viewing.
 * It can handle two scenarios:
 * 1. Direct client access (e.g., /admin/_/...)
 * 2. Workbook UUID access (e.g., /dashboard/uuid-123/...) which maps to a client via workbooks menu
 *
 * @returns {string | null} The client identifier or null if not found
 */
export function useGetClient() {
  const pathname = useLocation();
  const { user } = useAuth();
  const { current } = useWorkspace();

  const pathParts = pathname.pathname.split("/").filter(Boolean);
  const isWorkspaceRoute = pathParts[0] === "w";
  const moduleSeg = isWorkspaceRoute ? pathParts[3] : pathParts[0];
  const clientOrWorkbookUuid = isWorkspaceRoute
    ? decodePathSegment(pathParts[4])
    : pathParts[1];

  // First check if it's directly a client by looking in prefix arrays
  for (const [_, config] of Object.entries(clientConfig)) {
    if (clientOrWorkbookUuid && config.prefix.includes(clientOrWorkbookUuid)) {
      return clientOrWorkbookUuid;
    }
  }

  // Otherwise, search in workbooks menus if it's a workbook UUID
  for (const byWorkspace of Object.values(user?.workbooks ?? {})) {
    for (const [workspaceName, byMenu] of Object.entries(byWorkspace)) {
      for (const workbooks of Object.values(byMenu)) {
        if (clientOrWorkbookUuid && clientOrWorkbookUuid in workbooks) {
          const candidate = normalizeCode(workspaceName);
          if (
            Object.values(clientConfig).some((config) =>
              config.prefix.includes(candidate),
            )
          ) {
            return candidate;
          }
        }
      }
    }
  }

  // Dashboard fallback: use current workspace if it maps to a known client prefix
  if (moduleSeg === "dashboard") {
    const ws = normalizeCode(current?.code ?? "");
    if (
      ws &&
      Object.values(clientConfig).some((config) => config.prefix.includes(ws))
    ) {
      return ws;
    }
  }

  return null;
}

/**
 * Hook to get the client configuration for the current client
 *
 * @returns {TClientConfig | null} The merged client configuration or null if not found
 */
export function useClientConfig() {
  const client = useGetClient();

  if (!client) {
    return null;
  }

  // Find all configs that match the current client in their prefix array
  const matchingConfigs = Object.values(clientConfig).filter((config) =>
    config.prefix.includes(client),
  );

  if (matchingConfigs.length === 0) {
    return null;
  }

  // Merge all matching configs, preserving action order from declaration
  const mergedActions = matchingConfigs.flatMap((config) => config.actions);

  return {
    prefix: matchingConfigs[0].prefix, // Use prefix from first match
    actions: mergedActions,
  };
}

/**
 * Hook to get available actions for the current user
 *
 * This hook combines the current client detection with user group permissions
 * to return only the actions that the user is authorized to see and use.
 *
 * @returns {Array} Array of action objects that the user can access
 */
export function useClientActions() {
  const { user } = useAuth();
  const { current } = useWorkspace();
  const matchingConfig = useClientConfig();
  const userGroups = user?.groups || [];
  const wsCode: string | null = current?.code ?? null; // null = “All”
  const isAll = wsCode == null;

  const actions = useMemo(() => {
    if (!matchingConfig) return [];
    return matchingConfig.actions.filter((action) => {
      const groupOK = Array.isArray(action.groups)
        ? action.groups.some((requiredGroup) =>
            userGroups.includes(requiredGroup),
          )
        : false;
      if (!groupOK || (!isAll && !wsCode)) return false;
      return true;
    });
  }, [matchingConfig, userGroups, wsCode, isAll]);

  return {
    actions,
    hasActions: actions.length > 0,
  };
}

export function useCurrentPageFromUrl(): string | undefined {
  const location = useLocation();
  const parts = location.pathname.split("/").filter(Boolean);
  const isWorkspaceRoute = parts[0] === "w";
  const currentPage = isWorkspaceRoute
    ? (parts[3] ?? "unknown")
    : (parts[0] ?? "unknown");
  return currentPage;
}
